import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { MomentModule } from 'ngx-moment';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as fromUser from '../../../../authentication/store/selectors/authentication.selector';
import { PopupId } from '../../../../shared/models/popup.model';
import { UserPreferences } from '../../../../shared/models/user-preferences.model';
import { CurrencyDecimalPipe } from '../../../../shared/pipes/currency-decimal.pipe';
import { NgxDatePipe } from '../../../../shared/pipes/ngx-date.pipe';
import { UtilsService } from '../../../../shared/services/utils.service';
import { acknowledgePopupAction, trackEventAction } from '../../../../shared/store/actions/shared.action';
import * as fromShared from '../../../../shared/store/selectors/shared.selector';
import { PaymentEstimateV3 } from '../../../models/payment.model';
import { User, UserPlan } from '../../../models/user.model';
import { loadPaymentEstimateAction, upgradePlanAction } from '../../../store/actions/payment.action';
import * as fromPayment from '../../../store/selectors/payment.selector';
import { OfferToggleComponent } from '../../offer-toggle/offer-toggle.component';
@Component({
  selector: `app-tax-season-end-dialog`,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    CurrencyDecimalPipe,
    NgxDatePipe,
    MomentModule,
    OfferToggleComponent,
  ],
  templateUrl: `./tax-season-end-dialog.component.html`,
  styleUrl: `./tax-season-end-dialog.component.scss`,
})
export class TaxSeasonEndDialogComponent implements OnInit, OnDestroy {
  stablecoins: string[];
  fiats: string[];
  userPreferences: UserPreferences;

  user: User;
  frenchLocation: string;
  lastReportingDay: string;
  voucherCode: string;

  plan: UserPlan = `PLN500`;
  paymentEstimate: PaymentEstimateV3;
  fiscalYear: number;
  defaultFiscalYear: number = moment().year();

  loading = false;

  @Input() popupId: PopupId;
  @Input() upgradeFishingName: string;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly utilsService: UtilsService,
    private readonly cookieService: CookieService,
    private readonly sharedStore$: Store<fromShared.State>,
    private readonly userStore$: Store<fromUser.State>,
    private readonly paymentStore$: Store<fromPayment.State>,
    @Inject(MAT_DIALOG_DATA)
    @Optional()
    private readonly data: {
      popupId: PopupId;
    },
  ) {}

  ngOnInit(): void {
    if (this.data?.popupId) {
      this.popupId = this.data.popupId;
    }

    if (!this.utilsService.isFiscalYearSwitchable(this.defaultFiscalYear)) {
      this.defaultFiscalYear -= 1;
    }

    this.fiscalYear = this.defaultFiscalYear;

    this.userStore$
      .select(fromUser.selectUser)
      .pipe(
        takeUntil(this.destroy$),
        map((user: User) => {
          this.user = user;

          if (this.user) {
            this.computeVoucherCode();

            switch (this.user.fiscalResidency) {
              case `FR`:
                this.computeFrenchLocation();
                break;
              case `BE`:
                this.lastReportingDay = moment(`2025-07-15`).format(`Do MMM`);
                break;
              case `ES`:
                this.lastReportingDay = moment(`2025-06-30`).format(`Do MMM`);
                break;
            }
          }
        }),
      )
      .subscribe();

    this.paymentStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromPayment.selectPaymentEstimate),
        map((paymentEstimate: PaymentEstimateV3) => {
          this.paymentEstimate = paymentEstimate;
        }),
      )
      .subscribe();

    combineLatest([
      this.sharedStore$.select(fromShared.selectStableCoins),
      this.sharedStore$.select(fromShared.selectFiats),
      this.sharedStore$.select(fromShared.selectUserPreferences),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([stablecoins, fiats, userPreferences]) => {
          this.stablecoins = stablecoins;
          this.fiats = fiats;
          this.userPreferences = userPreferences;
        }),
      )
      .subscribe();

    this.userStore$
      .select(fromUser.selectUser)
      .pipe(
        takeUntil(this.destroy$),
        map((user: User) => {
          this.user = user;

          if (this.user) {
            this.computeVoucherCode();

            if (this.user.fiscalResidency === `FR`) {
              this.computeFrenchLocation();
            }
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  togglePlan(plan: UserPlan): void {
    this.plan = plan;

    this.getEstimate();
  }

  getEstimate(): void {
    this.paymentStore$.dispatch(
      loadPaymentEstimateAction({
        fiscalYear: this.fiscalYear,
        checkoutType: `PLAN`,
        requestedPlan: this.plan,
        code: this.voucherCode,
      }),
    );
  }

  computeFrenchLocation(): void {
    if (this.user?.localSettings?.fr?.postCode) {
      const postCode = +this.user.localSettings.fr.postCode.toString().replace(`_`, ``);

      if (postCode >= 1 && postCode <= 19) {
        this.frenchLocation = `FRENCH_ZONE_1`;
        this.lastReportingDay = moment(`2025-05-22`).format(`Do MMM`);
      }

      if (postCode >= 20 && postCode <= 54) {
        this.frenchLocation = `FRENCH_ZONE_2`;
        this.lastReportingDay = moment(`2025-05-28`).format(`Do MMM`);
      }

      if (postCode > 54) {
        this.frenchLocation = `FRENCH_ZONE_3`;
        this.lastReportingDay = moment(`2025-06-05`).format(`Do MMM`);
      }
    }
  }

  computeVoucherCode(): void {
    switch (this.popupId) {
      case `MKT-ENDZONE1-2025-FR`:
        this.voucherCode = `FRZONE12025`;
        break;
      case `MKT-ENDZONE2-2025-FR`:
        this.voucherCode = `FRZONE22025`;
        break;
      case `MKT-ENDZONE3-2025-FR`:
        this.voucherCode = `FRZONE32025`;
        break;
      case `MKT-ENDZONE-2025-BE`:
        this.voucherCode = `ENDBE2025`;
        break;
      case `MKT-ENDZONE-2025-ES`:
        this.voucherCode = `ENDESP2025`;
        break;
    }

    this.getEstimate();
  }

  floor(value: number): number {
    return Math.floor(value);
  }

  pay(): void {
    this.trackEvent();

    if (this.user.lockedToApple) {
      this.utilsService.openLockedMobileToast();
    } else {
      this.loading = true;

      // Acknowledge popup
      this.sharedStore$.dispatch(
        acknowledgePopupAction({
          popupId: this.popupId,
          acknowledgedFrom: `DESKTOP`,
        }),
      );

      const papCookie = this.utilsService.getCookie(`PAPVisitorId`);
      const financeAdsTrackingID = this.cookieService.get(`FinancialAdsVisitorId`);

      this.paymentStore$.dispatch(
        upgradePlanAction({
          paymentMethod: `STRIPE`,
          requestedPlan: this.plan,
          fiscalYear: this.fiscalYear,
          papCookie,
          financeAdsTrackingID,
          useVault: false,
          code: this.voucherCode,
        }),
      );
    }
  }

  trackEvent(): void {
    let upgradeName = ``;

    if (this.upgradeFishingName) {
      upgradeName = this.upgradeFishingName;
    } else {
      switch (this.popupId) {
        case `MKT-ENDZONE1-2025-FR`:
          upgradeName = `mkt_endzone1_fr`;
          break;
        case `MKT-ENDZONE2-2025-FR`:
          upgradeName = `mkt_endzone2_fr`;
          break;
        case `MKT-ENDZONE3-2025-FR`:
          upgradeName = `mkt_endzone3_fr`;
          break;
        case `MKT-ENDZONE-2025-BE`:
          upgradeName = `mkt_endzone_be`;
          break;
        case `MKT-ENDZONE-2025-ES`:
          upgradeName = `mkt_endzone_es`;
          break;
      }
    }

    this.sharedStore$.dispatch(
      trackEventAction({
        event: `upgrade_fishing`,
        data: {
          upgrade_name: upgradeName,
        },
      }),
    );
  }
}
